import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROLES, homePathFor } from '@/features/auth/roles';
import { useCartCount } from '@/features/cart/hooks/useCart';
import { storefrontApi } from '@/features/storefront/api/storefrontApi';
import { SiteIcon } from '@/features/site/components/SiteIcon';
import { mediaUrl } from '@/lib/media';
import { InstallAppButton } from '@/ui/InstallAppButton';
import { PlatformLogo } from '@/ui/PlatformLogo';
import { useBackToClose } from '@/ui/useBackToClose';

/**
 * The shop's header and footer, around every storefront page.
 *
 * Laid out as in the wireframe; what is in them comes from the shop's website
 * content. Navigation only offers the landing-page sections the shop has
 * switched on, so "Offers" never scrolls to a banner that is not there.
 */

const ANNOUNCE_DISMISSED = 'site:announcement-dismissed';

function navLinks(content) {
  return [
    { to: '/', label: 'Home', end: true },
    { to: '/products', label: 'Shop' },
    content.categories.enabled && { to: '/#categories', label: 'Categories', hash: true },
    content.new_arrivals.enabled && { to: '/#new', label: 'New Arrivals', hash: true },
    content.promo.enabled && { to: '/#offers', label: 'Offers', hash: true },
    content.about.enabled && { to: '/#about', label: 'About Us', hash: true },
  ].filter(Boolean);
}

function accountHrefFor(user) {
  if (!user) return '/login';
  return homePathFor(user.role);
}

/** Wider than this, a logo is taken to be a wordmark that already spells the
 *  name; anything squarer is an emblem that needs the name beside it. */
const WORDMARK_RATIO = 2.2;

export function SiteLogo({ site }) {
  const logo = mediaUrl(site.content.brand.logo_url);
  const [shape, setShape] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setShape(null);
    setFailed(false);
  }, [logo]);

  const showLogo = logo && !failed;
  return (
    <Link to="/" className="logo" aria-label={`${site.name} home`}>
      {showLogo ? (
        <img
          src={logo}
          alt=""
          className={shape === 'wordmark' ? 'logo-img logo-wordmark' : 'logo-img'}
          onLoad={(event) => {
            const { naturalWidth: w, naturalHeight: h } = event.currentTarget;
            setShape(h && w / h >= WORDMARK_RATIO ? 'wordmark' : 'emblem');
          }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="logo-mark">
          <SiteIcon name="bag" />
        </span>
      )}
      {shape !== 'wordmark' && <span className="logo-name">{site.name}</span>}
    </Link>
  );
}

function AnnouncementBar({ announcement }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(ANNOUNCE_DISMISSED) === '1';
    } catch {
      return false;
    }
  });

  const messages = announcement.messages.filter((message) => message.trim());
  if (!announcement.enabled || dismissed || messages.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(ANNOUNCE_DISMISSED, '1');
    } catch {
      // Private mode: it stays dismissed until the next page load.
    }
  };

  return (
    <div className="announce" role="region" aria-label="Store announcement">
      <div className="site-container">
        <p>
          {messages[0]}
          {messages.length > 1 && (
            <span className="hide-xs">
              {messages.slice(1).map((message) => (
                <span key={message}>
                  <span className="sep">|</span>
                  {message}
                </span>
              ))}
            </span>
          )}
        </p>
        <button className="announce-close" aria-label="Dismiss announcement" onClick={dismiss}>
          <SiteIcon name="close" />
        </button>
      </div>
    </div>
  );
}

function SearchOverlay({ open, onClose, replace }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  // Fetched only once the overlay is opened - it is not worth a request on
  // every page view for a box most visitors never open.
  const { data: categories } = useQuery({
    queryKey: ['storefront', 'categories'],
    queryFn: storefrontApi.categories,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const go = (to) => {
    onClose();
    navigate(to, { replace });
  };

  return (
    <div
      className={`search-overlay${open ? ' open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      aria-hidden={!open}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="search-box">
        <form
          className="search-field"
          onSubmit={(event) => {
            event.preventDefault();
            const q = new FormData(event.currentTarget).get('q')?.toString().trim();
            go(q ? `/products?q=${encodeURIComponent(q)}` : '/products');
          }}
        >
          <SiteIcon name="search" style={{ color: 'var(--site-muted)' }} />
          <input
            ref={inputRef}
            name="q"
            type="search"
            placeholder="Search for products, brands and more"
            aria-label="Search"
            tabIndex={open ? 0 : -1}
          />
          <button
            type="button"
            className="icon-btn"
            aria-label="Close search"
            onClick={onClose}
            tabIndex={open ? 0 : -1}
          >
            <SiteIcon name="close" />
          </button>
        </form>
        {(categories?.length ?? 0) > 0 && (
          <div className="search-tags">
            Popular:
            {categories.slice(0, 5).map((category) => (
              <a
                key={category.id}
                href={`/products?category=${category.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  go(`/products?category=${category.id}`);
                }}
              >
                {category.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Phones only: the always-visible search bar under the header row, as in the
 * shopping apps. A real field rather than a button that opens the overlay -
 * one tap fewer to start typing.
 */
function MobileSearch() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  // Shows the current search on the results page, so it can be refined.
  const current = pathname === '/products' ? (params.get('q') ?? '') : '';

  return (
    <form
      className="m-search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        const q = new FormData(event.currentTarget).get('q')?.toString().trim();
        event.currentTarget.querySelector('input')?.blur();
        navigate(q ? `/products?q=${encodeURIComponent(q)}` : '/products');
      }}
    >
      <SiteIcon name="search" />
      <input
        key={current}
        name="q"
        type="search"
        enterKeyHint="search"
        defaultValue={current}
        placeholder="Search for products, brands and more"
        aria-label="Search products"
      />
    </form>
  );
}

/**
 * Login / Sign up in the header: each opens on hover (or tap) to pick
 * Customer or Store. The menu sits flush under the button, so the pointer
 * can move into it.
 */
function RolePicker({ label, to, storeTo, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => !ref.current?.contains(event.target) && setOpen(false);
    const onKey = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className="role-pick hide-phone"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`btn role-pick-btn ${primary ? 'btn-primary' : 'btn-outline'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {label}
        <SiteIcon name="chev" className="i role-pick-chev" />
      </button>
      {open && (
        <div className="role-pick-menu" role="menu">
          <Link role="menuitem" to={to} onClick={() => setOpen(false)}>
            <SiteIcon name="user" /> Customer
          </Link>
          <Link role="menuitem" to={storeTo} onClick={() => setOpen(false)}>
            <SiteIcon name="bag" /> Store
          </Link>
        </div>
      )}
    </div>
  );
}

export function SiteHeader({ site }) {
  const { user } = useAuth();
  const itemCount = useCartCount();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Links in either panel take its Back entry - see useBackToClose.
  const replace = useBackToClose(drawerOpen || searchOpen, () => {
    setDrawerOpen(false);
    setSearchOpen(false);
  });
  const links = navLinks(site.content);
  const accountHref = accountHrefFor(user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close both panels on navigation, and lock the page behind them.
  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const locked = drawerOpen || searchOpen;
    document.body.style.overflow = locked ? 'hidden' : '';
    if (!locked) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen, searchOpen]);

  const isActive = (link) =>
    link.hash
      ? location.pathname === '/' && location.hash === link.to.slice(1)
      : link.end
        ? location.pathname === link.to && !location.hash
        : location.pathname.startsWith(link.to);

  return (
    <>
      <AnnouncementBar announcement={site.content.announcement} />

      <header className={`header${scrolled ? ' scrolled' : ''}`}>
        <div className="site-container">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <button
              className="icon-btn menu-toggle"
              aria-label="Open menu"
              aria-controls="site-drawer"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <SiteIcon name="menu" />
            </button>
            <SiteLogo site={site} />
          </div>

          <nav className="nav" aria-label="Primary">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={() => (isActive(link) ? 'active' : undefined)}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="header-actions">
            <InstallAppButton className="hide-md" />
            {/* Phones have the search bar below instead. */}
            <button
              className="icon-btn hide-phone"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <SiteIcon name="search" />
            </button>
            {user ? (
              <Link to={accountHref} className="icon-btn hide-sm" aria-label="Account">
                <SiteIcon name="user" />
              </Link>
            ) : (
              <>
                <RolePicker label="Login" to="/login" storeTo="/login?role=admin" />
                <RolePicker label="Sign up" to="/register" storeTo="/register-store" primary />
              </>
            )}
            {(!user || user.role === ROLES.CUSTOMER) && (
              <Link
                to={user ? '/account/wishlist' : '/login'}
                className="icon-btn hide-sm"
                aria-label="Wishlist"
              >
                <SiteIcon name="heart" />
              </Link>
            )}
            <Link to="/cart" className="icon-btn" aria-label={`Cart, ${itemCount} items`}>
              <SiteIcon name="cart" />
              {itemCount > 0 && <span className="badge-count">{itemCount}</span>}
            </Link>
          </div>
        </div>
        <MobileSearch />
      </header>

      <div
        className={`drawer-backdrop${drawerOpen ? ' open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        id="site-drawer"
        className={`drawer${drawerOpen ? ' open' : ''}`}
        aria-label="Mobile menu"
        aria-hidden={!drawerOpen}
      >
        <div className="drawer-top">
          <SiteLogo site={site} />
          <button className="icon-btn" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            <SiteIcon name="close" />
          </button>
        </div>
        <nav aria-label="Mobile">
          {links.map((link) => (
            <Link key={link.to} to={link.to} replace={replace} tabIndex={drawerOpen ? 0 : -1}>
              {link.label} <SiteIcon name="chev" />
            </Link>
          ))}
        </nav>
        <div className="drawer-foot">
          {user ? (
            <Link to={accountHref} replace={replace} tabIndex={drawerOpen ? 0 : -1}>
              <SiteIcon name="user" /> My Account
            </Link>
          ) : (
            <Link
              to="/login"
              replace={replace}
              className="btn btn-primary"
              tabIndex={drawerOpen ? 0 : -1}
            >
              Sign In
            </Link>
          )}
          {user?.role === ROLES.CUSTOMER && (
            <Link to="/account/wishlist" replace={replace} tabIndex={drawerOpen ? 0 : -1}>
              <SiteIcon name="heart" /> Wishlist
            </Link>
          )}
        </div>
      </aside>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} replace={replace} />
    </>
  );
}

const SOCIALS = [
  ['instagram', 'Instagram'],
  ['facebook', 'Facebook'],
  ['x', 'X'],
  ['youtube', 'YouTube'],
];

export function SiteFooter({ site }) {
  const { brand, footer, about } = site.content;
  const socials = SOCIALS.filter(([key]) => footer.social[key]);
  const payments = footer.payment_methods.filter((method) => method.trim());

  return (
    <footer className="footer">
      <div className="site-container">
        <div className="footer-top">
          <div className="footer-brand">
            <SiteLogo site={site} />
            {brand.tagline && <p>{brand.tagline}</p>}
            {(footer.contact_email || footer.contact_phone) && (
              <div className="footer-contact">
                {footer.contact_email && (
                  <a href={`mailto:${footer.contact_email}`}>{footer.contact_email}</a>
                )}
                {footer.contact_phone && (
                  <a href={`tel:${footer.contact_phone.replace(/\s+/g, '')}`}>
                    {footer.contact_phone}
                  </a>
                )}
              </div>
            )}
            {socials.length > 0 && (
              <div className="socials">
                {socials.map(([key, label]) => (
                  <a
                    key={key}
                    href={footer.social[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                  >
                    <SiteIcon name={key} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4>Shop</h4>
            <ul>
              <li>
                <Link to="/products">All Products</Link>
              </li>
              <li>
                <Link to="/products?sort=newest">New Arrivals</Link>
              </li>
              <li>
                <Link to="/products?sort=top_rated">Best Sellers</Link>
              </li>
              <li>
                <Link to="/products?sort=discount">Offers</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Customer Service</h4>
            <ul>
              <li>
                <Link to="/help">Help Center</Link>
              </li>
              <li>
                <Link to="/account/orders">My Orders</Link>
              </li>
              <li>
                <Link to="/cart">Cart</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              {about.enabled && (
                <li>
                  <Link to="/#about">About Us</Link>
                </li>
              )}
              <li>
                <Link to="/contact">Contact Us</Link>
              </li>
              <li>
                <Link to="/help">FAQs</Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © {new Date().getFullYear()} {site.name}. All rights reserved.
          </span>
          {payments.length > 0 && (
            <div className="pay" aria-label="Accepted payments">
              {payments.map((method) => (
                <span key={method}>{method}</span>
              ))}
            </div>
          )}
          <span className="powered">
            Powered by <PlatformLogo size={22} withName nameClassName="text-[12.5px]" />
          </span>
        </div>
      </div>
    </footer>
  );
}
